import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createOrganisation,
  inviteMember,
  changeMemberRole,
  setMemberCustomerIds,
  removeMember,
  updateOrganisationUiFlags,
  type OrganisationUiFlags,
  type CreateOrganisationInput,
  type InviteMemberInput,
  type OrganisationRow,
  type CreatedOrganisation,
} from '@/api/endpoints/admin';
import type { Role } from '@revcall/shared';

/**
 * Admin / member mutations. No optimistic updates here, except the UI flag
 * switch below (a toggle must move when clicked) — these are
 * low-frequency operations and the user benefits more from seeing the
 * authoritative server response (e.g. the new membership ID, the
 * permission errors) than from instant UI feedback.
 */

export function useCreateOrganisation() {
  const qc = useQueryClient();
  return useMutation<CreatedOrganisation, Error, CreateOrganisationInput>({
    mutationFn: (input) => createOrganisation(input),
    onSettled() {
      qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
    },
  });
}

interface UiFlagsVars { id: string; patch: Partial<OrganisationUiFlags> }
type OrganisationsCache = { organisations: OrganisationRow[]; total: number; invalid: number };

/**
 * SR2-7 switch: optimistic, so the switch moves at once; on failure the row
 * goes back to its previous flags and the caller shows the error (DES-12).
 */
export function useUpdateOrganisationUiFlags() {
  const qc = useQueryClient();
  const key = ['admin', 'organisations'];
  const setRowFlags = (id: string, flags: (prev: OrganisationUiFlags) => OrganisationUiFlags) => {
    const cache = qc.getQueryData<OrganisationsCache>(key);
    if (!cache) return;
    qc.setQueryData<OrganisationsCache>(key, {
      ...cache,
      organisations: cache.organisations.map((o) => (o.id === id ? { ...o, ui_flags: flags(o.ui_flags) } : o)),
    });
  };
  return useMutation<OrganisationUiFlags, Error, UiFlagsVars, { previous?: OrganisationUiFlags }>({
    mutationFn: ({ id, patch }) => updateOrganisationUiFlags(id, patch),
    async onMutate({ id, patch }) {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<OrganisationsCache>(key)?.organisations.find((o) => o.id === id)?.ui_flags;
      setRowFlags(id, (cur) => ({ ...cur, ...patch }));
      return { previous };
    },
    onError(_err, { id }, ctx) {
      // Only this row goes back: another row switched meanwhile keeps its state.
      if (ctx?.previous) setRowFlags(id, () => ctx.previous!);
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useInviteMember(organisationId: string) {
  const qc = useQueryClient();
  return useMutation<boolean, Error, InviteMemberInput>({
    mutationFn: (input) => inviteMember(organisationId, input),
    onSettled() {
      qc.invalidateQueries({ queryKey: ['organisation', organisationId, 'members'] });
    },
  });
}

interface ChangeRoleVars {
  membershipId: string;
  role: Role;
}

export function useChangeMemberRole(organisationId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, ChangeRoleVars>({
    mutationFn: ({ membershipId, role }) =>
      changeMemberRole(organisationId, membershipId, role),
    onSettled() {
      qc.invalidateQueries({ queryKey: ['organisation', organisationId, 'members'] });
    },
  });
}

interface SetCustomerIdsVars {
  membershipId: string;
  customerIds: string[];
}

export function useSetMemberCustomerIds(organisationId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, SetCustomerIdsVars>({
    mutationFn: ({ membershipId, customerIds }) =>
      setMemberCustomerIds(organisationId, membershipId, customerIds),
    onSettled() {
      qc.invalidateQueries({ queryKey: ['organisation', organisationId, 'members'] });
    },
  });
}

export function useRemoveMember(organisationId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (membershipId) => removeMember(organisationId, membershipId),
    onSettled() {
      qc.invalidateQueries({ queryKey: ['organisation', organisationId, 'members'] });
    },
  });
}
